/**
 * Project passport, rebuilt for the flat wiring schema.
 *
 * Two independent passports: `plan` (is_fact === false) and `fact` (is_fact === true),
 * each split into "ленты" (streams) by org_id (no org -> the "ФЛ" stream).
 *
 * Per stream, aggregate wiring `sum` by article_id (mirrors the reference pandas):
 *   projectSum            article_id in [79, 80, 121]
 *   nds                   article_id == 72
 *   cashlessSubcontract   article_id in [61, 62]
 *   irretrievableGuarantee article_id in [67, 68, 70, 78]
 *   reserveCashless       article_id == 69 and NOT is_cash
 *   nonExpertCash         article_id == 701
 *   bonusScheme           article_id == 65
 *   externalExperts       article_id == 64
 *   otherProject          article_id == 66
 *   reserveCash           article_id == 69 and is_cash
 *
 *   vi = projectSum - nds - cashlessSubcontract - irretrievableGuarantee
 *        - reserveCashless - nonExpertCash
 *
 * Taxes / Pri per stream:
 *   taxBase              = max(vi, 0)
 *   <rate>Sum            = taxBase * rate% / 100   (rate from RateConfig by org_id / "FL")
 *   taxesTotal           = sum of all <rate>Sum
 *   viAfterTaxes         = vi - taxesTotal
 *   pri                  = viAfterTaxes - bonusScheme - externalExperts
 *                          - otherProject - reserveCash
 *
 *   project Pri (plan / fact) = sum of stream `pri` = passport.<plan|fact>.total.pri
 */

import { RATE_ARTICLES, rateOf } from '@/modules/RateConfig';

const ART = {
  projectSum: [79, 80, 121],
  nds: [72],
  cashlessSubcontract: [61, 62],
  irretrievableGuarantee: [67, 68, 70, 78],
  nonExpertCash: [701],
  bonusScheme: [65],
  externalExperts: [64],
  otherProject: [66],
  RESERVE: 69,
};

const RATE_KEYS = RATE_ARTICLES.map((a) => a.key);
const rateSumKey = (k) => `${k}Sum`;

// ---- per-row wiring filters (for the check-boxes over passport rows) ----
// key -> predicate over a hydrated wire (raw article_id / is_cash kept on w.data).
// Only "leaf" rows backed by real wirings appear here — computed rows
// (vi, taxes by rate, viAfterTaxes, pri, taxesTotal) are not filterable.
const artInSet = (ids) => (w) => ids.includes(Number(w.data && w.data.article_id));
const isReserve = (w) => Number(w.data && w.data.article_id) === ART.RESERVE;

export const ROW_WIRING_MATCHERS = {
  projectSum: artInSet(ART.projectSum),
  nds: artInSet(ART.nds),
  cashlessSubcontract: artInSet(ART.cashlessSubcontract),
  irretrievableGuarantee: artInSet(ART.irretrievableGuarantee),
  reserveCashless: (w) => isReserve(w) && !(w.data && w.data.is_cash),
  nonExpertCash: artInSet(ART.nonExpertCash),
  bonusScheme: artInSet(ART.bonusScheme),
  externalExperts: artInSet(ART.externalExperts),
  otherProject: artInSet(ART.otherProject),
  reserveCash: (w) => isReserve(w) && !!(w.data && w.data.is_cash),
};

export const FILTERABLE_ROW_KEYS = Object.keys(ROW_WIRING_MATCHERS);

// keep only wirings matching at least one of the selected passport-row keys;
// empty / missing selection -> the list is returned unchanged
export function filterWiringsByRowKeys(wirings, keys) {
  if (!keys || !keys.length) return wirings || [];
  const matchers = keys.map((k) => ROW_WIRING_MATCHERS[k]).filter(Boolean);
  if (!matchers.length) return wirings || [];
  return (wirings || []).filter((w) => w && w.data && matchers.some((m) => m(w)));
}

const artId = (w) => Number(w.data && w.data.article_id);
const isCash = (w) => !!(w.data && w.data.is_cash);
const sumOf = (list, pred) =>
  list.reduce((s, w) => (pred(w) ? s + (Number(w.data.sum) || 0) : s), 0);
const inSet = (set) => (w) => set.includes(artId(w));

// every numeric field a stream / total carries (used to zero-init and to sum totals)
export const METRIC_KEYS = [
  'projectSum',
  'nds',
  'cashlessSubcontract',
  'irretrievableGuarantee',
  'reserveCashless',
  'nonExpertCash',
  'vi',
  ...RATE_KEYS.map(rateSumKey),
  'taxesTotal',
  'viAfterTaxes',
  'bonusScheme',
  'externalExperts',
  'otherProject',
  'reserveCash',
  'pri',
];

const zeroMetrics = () => METRIC_KEYS.reduce((a, k) => ((a[k] = 0), a), {});

function computeStreamMetrics(list, orgId, rateConfig) {
  const m = {
    projectSum: sumOf(list, inSet(ART.projectSum)),
    nds: sumOf(list, inSet(ART.nds)),
    cashlessSubcontract: sumOf(list, inSet(ART.cashlessSubcontract)),
    irretrievableGuarantee: sumOf(list, inSet(ART.irretrievableGuarantee)),
    reserveCashless: sumOf(list, (w) => artId(w) === ART.RESERVE && !isCash(w)),
    nonExpertCash: sumOf(list, inSet(ART.nonExpertCash)),
    bonusScheme: sumOf(list, inSet(ART.bonusScheme)),
    externalExperts: sumOf(list, inSet(ART.externalExperts)),
    otherProject: sumOf(list, inSet(ART.otherProject)),
    reserveCash: sumOf(list, (w) => artId(w) === ART.RESERVE && isCash(w)),
  };

  m.vi =
    m.projectSum -
    m.nds -
    m.cashlessSubcontract -
    m.irretrievableGuarantee -
    m.reserveCashless -
    m.nonExpertCash;

  // taxes by rate
  const taxBase = Math.max(m.vi, 0);
  let taxesTotal = 0;
  for (const key of RATE_KEYS) {
    const pct = rateOf(rateConfig, orgId, key) || 0; // missing rate -> 0
    const s = (taxBase * pct) / 100;
    m[rateSumKey(key)] = s;
    taxesTotal += s;
  }
  m.taxesTotal = taxesTotal;
  m.viAfterTaxes = m.vi - taxesTotal;

  m.pri = m.viAfterTaxes - m.bonusScheme - m.externalExperts - m.otherProject - m.reserveCash;
  return m;
}

function orgNameOf(list, key) {
  if (key === 'FL') return 'ФЛ';
  const o = list.find((w) => w.data && w.data.organization)?.data.organization;
  return (o && (o.shortName || o.fullName)) || `#${key}`;
}

function buildPassport(wirings, rateConfig) {
  const groups = new Map();
  for (const w of wirings) {
    if (!w || !w.data) continue;
    const key = w.data.org_id == null ? 'FL' : String(w.data.org_id);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(w);
  }

  const streams = [...groups.entries()]
    .map(([key, list]) => {
      const orgId = key === 'FL' ? null : Number(key);
      return {
        orgKey: key,
        orgId,
        orgName: orgNameOf(list, key),
        count: list.length,
        ...computeStreamMetrics(list, orgId, rateConfig),
      };
    })
    .sort((a, b) =>
      a.orgKey === 'FL' ? 1 : b.orgKey === 'FL' ? -1 : a.orgName.localeCompare(b.orgName, 'ru')
    );

  const total = streams.reduce((acc, s) => {
    for (const k of METRIC_KEYS) acc[k] += s[k];
    return acc;
  }, zeroMetrics());

  return { streams, total };
}

// wirings: hydrated project wirings; rateConfig: { "<orgId>"|"FL": { <rateKey>: % } }
export function computePassport(wirings, rateConfig) {
  const list = (wirings || []).filter((w) => w && w.data);
  return {
    plan: buildPassport(list.filter((w) => !w.data.is_fact), rateConfig || {}),
    fact: buildPassport(list.filter((w) => w.data.is_fact), rateConfig || {}),
  };
}

// row descriptors, in display order
export const PASSPORT_ROWS = [
  { key: 'projectSum', label: 'Сумма проекта', heading: true },
  { key: 'nds', label: '1.1 НДС согласно контракту за вычетом НДС соисполнителя' },
  { key: 'cashlessSubcontract', label: '1.2 Безналичная оплата соисполнителей' },
  { key: 'irretrievableGuarantee', label: '1.3 Безвозвратное обеспечение' },
  { key: 'reserveCashless', label: '1.4 Безналичный резерв' },
  { key: 'nonExpertCash', label: '1.5 Неэкспертные наличные расходы' },
  { key: 'vi', label: 'Vi — сумма контракта без НДС', heading: true },

  ...RATE_ARTICLES.map((a, i) => ({ key: rateSumKey(a.key), label: `2.${i + 1} ${a.label} (по ставке)`, indent: true })),
  { key: 'taxesTotal', label: '2 Всего налоги и накладные по ставкам' },
  { key: 'viAfterTaxes', label: 'Vi после налогов', heading: true },

  { key: 'bonusScheme', label: '3.1 Схема бонусов' },
  { key: 'externalExperts', label: '3.2 Оплата внешних экспертов' },
  { key: 'otherProject', label: '3.3 Другие проектные расходы' },
  { key: 'reserveCash', label: '3.4 Резерв (нал)' },
  { key: 'pri', label: 'Pri — прибыль проекта', heading: true },
];
